import { Fragment, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { MuiChipsInput } from "mui-chips-input";
import { CustomInputField } from "../../ui";
import { exportToExcel, generateCombinations } from "./utils";
import { statusLabels, apiClient, StatusEnum } from "../../shared";
import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";

const VariantAttributesSection = () => {
  const { hasPermission } = useAuth();
  const { control, watch, setValue, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "variantAttributes",
  });

  const productName = watch("name");
  const stockKeepingUnitPrefix = watch("stockKeepingUnitPrefix");
  const variantAttributes = watch("variantAttributes");
  const productVariants = watch("productVariants");
  const [productVariantAttributes, setProductVariantAttributes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProductVariantAttributes = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiClient.get("product-variant-attributes", {
          params: {
            status: StatusEnum.ACTIVE,
          },
        });
        setProductVariantAttributes(data);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductVariantAttributes();
  }, []);

  const handleDownloadTemplate = async (filled = false) => {
    const isValid = await trigger(["variantAttributes"]);
    if (!isValid) {
      return;
    }

    let combinations = generateCombinations(
      variantAttributes,
      productName,
      stockKeepingUnitPrefix
    );

    combinations = combinations.map((combination) => {
      const existingVariant = productVariants.find(
        (variant) => variant.sku === combination.SKU
      );

      // Add price fields based on permissions
      if (hasPermission(PermissionEnum.UPDATE_PRODUCT_RETAIL_PRICE)) {
        combination.retailPrice =
          filled && existingVariant?.retailPrice !== undefined
            ? existingVariant.retailPrice
            : 0;
      }

      if (hasPermission(PermissionEnum.UPDATE_PRODUCT_DISTRIBUTOR_PRICE)) {
        combination.distributorPrice =
          filled && existingVariant?.distributorPrice !== undefined
            ? existingVariant.distributorPrice
            : 0;
      }

      // Add status for filled templates with existing variants
      if (filled && existingVariant) {
        combination.status = statusLabels[existingVariant.status];
      }

      return combination;
    });

    exportToExcel(
      productName,
      stockKeepingUnitPrefix,
      variantAttributes,
      combinations
    );
  };

  const getFilteredProductVariantAttributes = (index) => {
    const selectedAttributeIds = variantAttributes
      .filter((_, i) => i !== index)
      .map((attr) => attr.attribute?.id);

    return productVariantAttributes.filter(
      (attr) => !selectedAttributeIds.includes(attr.id)
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 3,
        border: "1px solid #ccc",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between"
        }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Isi & Download Template
        </Typography>
        {fields.length <= 3 && (
          <Button
            variant="outlined"
            onClick={() => {
              append({ attribute: null, values: [] });
            }}
          >
            + Attribut
          </Button>
        )}
      </Stack>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {fields.map((field, index) => {
          const attribute = watch(`variantAttributes[${index}].attribute`);

          return (
            <Fragment key={field.id}>
              <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                <Controller
                  control={control}
                  name={`variantAttributes[${index}].attribute`}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      value={field.value}
                      onChange={(_, newValue) => {
                        setValue(
                          `variantAttributes[${index}].attribute`,
                          newValue
                        );
                      }}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, selectedOption) =>
                        option.id === selectedOption.id
                      }
                      disableClearable={true}
                      options={getFilteredProductVariantAttributes(index)}
                      renderInput={(params) => (
                        <CustomInputField
                          {...params}
                          label={`Attribut ${index + 1}`}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            ...params.slotProps.input,
                            placeholder: "Pilih",
                            endAdornment: (
                              <>
                                {isLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.slotProps.input.endAdornment}
                              </>
                            ),
                          }}
                          required={index === 0}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                <Stack direction="row" spacing={2} sx={{ mt: 3.5 }}>
                  <Controller
                    control={control}
                    name={`variantAttributes[${index}].values`}
                    render={({ field, fieldState }) => (
                      <MuiChipsInput
                        {...field}
                        value={variantAttributes[index].values}
                        fullWidth
                        size="small"
                        hideClearAll={true}
                        disabled={!attribute}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        validate={(chip) =>
                          !variantAttributes[index].values.includes(chip)
                        }
                        onChange={(chips) => {
                          field.onChange(chips);
                        }}
                      />
                    )}
                  />
                  {variantAttributes.length > 1 && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton onClick={() => remove(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Fragment>
          );
        })}
      </Grid>
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Tooltip
          title="Mohon mengisi nama dan SKU dari produk terlebih dahulu"
          disableHoverListener={
            Boolean(productName) && Boolean(stockKeepingUnitPrefix)
          }
        >
          <Box sx={{ width: "100%" }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!productName || !stockKeepingUnitPrefix}
              onClick={() => handleDownloadTemplate(false)}
            >
              Download Empty Template
            </Button>
          </Box>
        </Tooltip>
        {productVariants.length > 0 && (
          <Tooltip
            title="Mohon mengisi nama dan SKU dari produk terlebih dahulu"
            disableHoverListener={
              Boolean(productName) && Boolean(stockKeepingUnitPrefix)
            }
          >
            <Box sx={{ width: "100%" }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={!productName || !stockKeepingUnitPrefix}
                onClick={() => handleDownloadTemplate(true)}
              >
                Download Filled Template
              </Button>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

export default VariantAttributesSection;
